// Importamos `track` desde el sub-package `/react` (mismo que el resto
// del proyecto usa para <Analytics />). Vite ya tiene optimizado ese
// chunk, así que evitamos una re-optimización extra que podría dejar
// chunks viejos cacheados en el browser durante el HMR.
import { track as vercelTrack } from "@vercel/analytics/react";
import { readConsent } from "@/lib/cookie-consent";
import { trackMeta, type MetaEventName, type MetaParams } from "@/lib/meta-pixel";

// Wrapper de eventos custom para el sitio público. Toda llamada se
// gatea contra `cookie-consent.ts > readConsent()` — sin consentimiento
// de analítica, no se emite nada. RGPD/ePrivacy. El gating se hace en
// runtime cada llamada, no al montar, para que si el usuario consiente
// más tarde los eventos siguientes ya emitan sin tener que recargar.
//
// Convención de nombres (documentada en docs/analytics-events.md):
//   - Verbos en pasado: `cta_pricing_clicked`, `form_submitted`.
//   - snake_case.
//   - Properties también snake_case.
//
// Emisores actuales tras retirar /book-demo (la reunión previa ya no existe;
// todo CTA de producto lleva al alta self-serve):
//   1. cta_pricing_clicked      { plan?, location } — clic en CTA -> /pricing
//   2. cta_demo_clicked         { plan?, location } — clic en CTA -> /contact (sales-led)
//   3. language_switched        { from, to }
//   4. module_clicked           { module_key, location }
//   5. form_submitted           { form, segment? }

export interface CtaDemoEvent {
  plan?: string;
  location: string;
}

export interface CtaPricingEvent {
  plan?: string;
  /** Tipo de parque seleccionado en el pricing hub (events / park / mix). Legacy. */
  park_type?: string;
  /** Pack del pricing hub (starter / growth). */
  pack?: string;
  /** Ciclo de facturación (monthly / annual). */
  billing?: string;
  /** Módulos opcionales seleccionados, separados por coma. */
  modules?: string;
  location: string;
}

export interface PricingTabSelectedEvent {
  park_type: string;
}

export interface PricingBillingSelectedEvent {
  billing: string;
}

export interface LanguageSwitchedEvent {
  from: string;
  to: string;
}

export interface ModuleClickedEvent {
  module_key: string;
  location: "hub_gallery" | "header_dropdown" | "features_nav" | "features_section";
}

export interface FormSubmittedEvent {
  form: "contact" | "subscribe";
  segment?: string;
}

// Embudo del checkout (LDT-694, src/lib/checkout-funnel.ts). Solo nombres de
// paso y motivos: nunca lo que el visitante escribe.
export interface CheckoutStepViewedEvent {
  step: string;
  step_index: number;
}

export interface CheckoutErrorEvent {
  reason: string;
}

export interface CheckoutAbandonedEvent {
  last_step: string;
}

type EventMap = {
  cta_demo_clicked: CtaDemoEvent;
  // Clic en un CTA que lleva a /pricing a elegir plan.
  cta_pricing_clicked: CtaPricingEvent;
  // Clic en el CTA de un pack: abre el checkout.
  cta_pricing_pack_clicked: CtaPricingEvent;
  // Clic en "Ir al pago": el visitante sale hacia la pasarela.
  cta_checkout_started: CtaPricingEvent;
  cta_hero_pricing_clicked: CtaPricingEvent;
  language_switched: LanguageSwitchedEvent;
  module_clicked: ModuleClickedEvent;
  form_submitted: FormSubmittedEvent;
  pricing_tab_selected: PricingTabSelectedEvent;
  pricing_billing_selected: PricingBillingSelectedEvent;
  // Cada paso del checkout que ve el visitante, una vez por apertura (LDT-694).
  checkout_step_viewed: CheckoutStepViewedEvent;
  // Error al comprobar la contraseña o al pulsar "Ir al pago".
  checkout_error: CheckoutErrorEvent;
  // Cierra el modal (o la pestaña) sin haber salido a la pasarela.
  checkout_abandoned: CheckoutAbandonedEvent;
};

// LDT-696 (épica LDT-691): qué eventos del sitio público llegan también a
// Meta, y como qué. Es UNA tabla y no llamadas a `fbq` repartidas por los
// componentes; lo que no está aquí no sale hacia Meta. Los parámetros se
// construyen con claves fijas a partir de las properties del evento, que ya
// son nombres de CTA, de módulo o de formulario, nunca lo que escribe nadie.
//
// Fuera a propósito:
//   - `form_submitted {form: "contact"}`: su `Lead` lo manda /contact con el
//     mismo event_id que la API de conversiones (LDT-693); aquí se duplicaría.
//   - `cta_pricing_pack_clicked`, `cta_checkout_started`, `checkout_*`: el
//     embudo del checkout ya va a Meta con sus propios eventos (LDT-694).
//   - `language_switched`, `pricing_tab_selected`, `pricing_billing_selected`:
//     ruido para Meta; solo Vercel/Clarity.
type MetaMapping<K extends keyof EventMap> = (
  props: EventMap[K],
) => { event: MetaEventName; params: MetaParams } | null;

export const META_MAP: { [K in keyof EventMap]?: MetaMapping<K> } = {
  // Intención de ver precios.
  cta_pricing_clicked: (p) => ({ event: "ViewContent", params: { content_name: "pricing", location: p.location } }),
  cta_hero_pricing_clicked: (p) => ({
    event: "ViewContent",
    params: { content_name: "pricing", location: p.location },
  }),
  // Qué módulos interesan: útil para públicos.
  module_clicked: (p) => ({
    event: "ViewContent",
    params: { content_name: p.module_key, content_category: "module" },
  }),
  // «Hablar con ventas».
  cta_demo_clicked: (p) => ({ event: "Contact", params: { location: p.location } }),
  // Solo navegador: la lista de espera no pasa por ninguna función con CAPI.
  form_submitted: (p) => (p.form === "subscribe" ? { event: "Lead", params: { content_name: "subscribe" } } : null),
};

export function trackEvent<K extends keyof EventMap>(
  name: K,
  properties: EventMap[K],
): void {
  // 0. Meta, con SU consentimiento (marketing), independiente del de
  //    analítica: `trackMeta` lo comprueba en cada llamada (LDT-692), igual que
  //    la ruta del sitio público. Analítica sí y marketing no → Vercel/Clarity
  //    sí, Meta no; y al revés.
  const toMeta = (META_MAP[name] as MetaMapping<K> | undefined)?.(properties);
  if (toMeta) trackMeta(toMeta.event, toMeta.params);

  // 1. Comprobar consentimiento. Sin él, salimos en silencio.
  const consent = readConsent();
  if (!consent || !consent.analytics) return;

  // 2. Vercel Analytics admite properties como Record<string, primitive>.
  try {
    vercelTrack(name, properties as unknown as Record<string, string | number | boolean | null>);
  } catch (err) {
    console.warn("[analytics] vercel track failed:", err);
  }

  // 3. Si Microsoft Clarity está cargado (gateado por consent también),
  //    duplicamos el evento allí. Clarity no soporta properties nested;
  //    las setteamos como custom tags para filtrar en su panel.
  if (typeof window !== "undefined" && "clarity" in window) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clarity = (window as any).clarity as (...args: unknown[]) => void;
      if (typeof clarity === "function") {
        clarity("event", name);
        // Doble cast por `unknown`: las interfaces de evento no llevan index
        // signature, así que TypeScript no las considera comparables con
        // Record<string, unknown> aunque en runtime lo sean.
        for (const [k, v] of Object.entries(properties as unknown as Record<string, unknown>)) {
          if (v != null) clarity("set", k, String(v));
        }
      }
    } catch (err) {
      console.warn("[analytics] clarity track failed:", err);
    }
  }
}
